/**
 * IntegrationKeyGuide — Self-service "How do I get these keys?" assistant.
 *
 * Drop next to any integration / API-key input. Opens a modal with three modes:
 *   1. Read   — step-by-step written instructions
 *   2. Listen — pre-generated MP3 (Kaharian TTS) with browser SpeechSynthesis fallback
 *   3. Watch  — embedded video (YouTube / Vimeo / Google Drive / MP4) set by Super Admin
 *
 * Usage:
 *   <IntegrationKeyGuide provider="meta" />
 *   <IntegrationKeyGuide provider="google_ads" variant="inline" />
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  BookOpen, Volume2, Square, Loader2, Video, X, ExternalLink,
  HelpCircle, CheckCircle2, AlertTriangle, Pencil, Save, Clock,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const IntegrationKeyGuide = ({ provider, variant = 'button', label = null }) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('read');
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [ttsSource, setTtsSource] = useState(null);
  const [editingVideo, setEditingVideo] = useState(false);
  const [videoUrlDraft, setVideoUrlDraft] = useState('');
  const [savingVideo, setSavingVideo] = useState(false);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const audioRef = useRef(null);

  const fetchGuide = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/integration-guides/${provider}`);
      setGuide(data);
      setVideoUrlDraft(data.video_url_raw || data.video_url || '');
    } catch (err) {
      toast.error('Failed to load guide');
    } finally {
      setLoading(false);
    }
  }, [provider]);

  useEffect(() => {
    if (open && !guide) fetchGuide();
  }, [open, guide, fetchGuide]);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      if (audioRef.current instanceof Audio) audioRef.current.pause();
      audioRef.current = null;
    }
    window.speechSynthesis?.cancel();
    setPlaying(false);
    setTtsSource(null);
  }, []);

  const handleListen = useCallback(async () => {
    if (playing) {
      stopAudio();
      return;
    }
    if (!guide) return;

    // Step 1 — try the pre-generated MP3 (instant, zero credits)
    try {
      const url = `${API}/api/static/integration_audio/${provider}.mp3`;
      const audio = new Audio(url);
      await new Promise((resolve, reject) => {
        audio.oncanplaythrough = resolve;
        audio.onerror = reject;
        audio.load();
        setTimeout(reject, 2500);
      });
      audioRef.current = audio;
      audio.onended = () => { setPlaying(false); setTtsSource(null); audioRef.current = null; };
      audio.onerror = () => { setPlaying(false); setTtsSource(null); audioRef.current = null; };
      await audio.play();
      setPlaying(true);
      setTtsSource('premium');
      return;
    } catch {
      /* fall through to browser TTS */
    }

    // Step 2 — browser SpeechSynthesis fallback (free)
    if ('speechSynthesis' in window) {
      const parts = [
        `How to get your ${guide.name} API keys.`,
        guide.summary || '',
        ...(guide.steps || []).map((s, i) => `Step ${i + 1}: ${s.title}. ${s.body}`),
      ];
      const utterance = new SpeechSynthesisUtterance(parts.join(' '));
      utterance.rate = 0.95;
      const voices = window.speechSynthesis.getVoices();
      const preferred = voices.find(v => v.name.includes('Google') && v.lang.startsWith('en'))
        || voices.find(v => v.lang.startsWith('en-US'));
      if (preferred) utterance.voice = preferred;
      utterance.onend = () => { setPlaying(false); setTtsSource(null); audioRef.current = null; };
      utterance.onerror = () => { setPlaying(false); setTtsSource(null); audioRef.current = null; };
      audioRef.current = utterance;
      window.speechSynthesis.speak(utterance);
      setPlaying(true);
      setTtsSource('browser');
    } else {
      toast.error('Audio not available — please use the Read tab');
    }
  }, [playing, guide, provider, stopAudio]);

  const handleSaveVideo = async () => {
    setSavingVideo(true);
    try {
      const { data } = await axios.put(
        `${API}/api/integration-guides/${provider}/video`,
        { video_url: videoUrlDraft },
      );
      setGuide(g => ({ ...g, video_url: data.video_url, video_url_raw: data.video_url_raw }));
      setEditingVideo(false);
      toast.success('Video URL saved for all subscribers');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save video URL');
    } finally {
      setSavingVideo(false);
    }
  };

  const handleGenerateAudio = async () => {
    setGeneratingAudio(true);
    try {
      const { data } = await axios.post(
        `${API}/api/integration-guides/${provider}/generate-audio`,
        { force: true },
      );
      toast.success(`Audio generated (${Math.round(data.size_bytes / 1024)} KB)`);
      setGuide(g => ({ ...g, audio_available: true, audio_url: data.url }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Audio generation failed');
    } finally {
      setGeneratingAudio(false);
    }
  };

  const handleClose = () => {
    stopAudio();
    setEditingVideo(false);
    setOpen(false);
  };

  // ─── Trigger button (compact icon or full button) ───
  const triggerEl = variant === 'inline' ? (
    <button
      onClick={() => setOpen(true)}
      className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-700 hover:underline font-medium"
      data-testid={`integration-guide-trigger-${provider}`}
    >
      <HelpCircle className="h-3 w-3" />
      {label || 'How do I get these keys?'}
    </button>
  ) : (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => setOpen(true)}
      className="text-xs gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-300"
      data-testid={`integration-guide-trigger-${provider}`}
    >
      <HelpCircle className="h-3.5 w-3.5" />
      {label || 'How do I get these keys?'}
    </Button>
  );

  return (
    <>
      {triggerEl}

      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          onClick={handleClose}
          data-testid={`integration-guide-modal-${provider}`}
        >
          <div className="absolute inset-0 bg-black/50 animate-fadeIn" />
          <div
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] animate-fadeIn overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-600 px-5 py-4 flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <HelpCircle className="h-4 w-4 text-white/80" />
                  <span className="text-[11px] uppercase tracking-wider text-white/70">Step-by-step guide</span>
                </div>
                <h2 className="text-lg font-heading font-bold text-white truncate">
                  How to get your {guide?.name || provider} API keys
                </h2>
                {guide && (
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-white/80">
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> ~{guide.estimated_time_minutes} min</span>
                    <span>•</span>
                    <span>{guide.category}</span>
                  </div>
                )}
              </div>
              <button
                onClick={handleClose}
                className="text-white/70 hover:text-white transition-colors flex-shrink-0 ml-3"
                data-testid="integration-guide-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-100 px-2 flex items-center gap-1 bg-gray-50">
              {[
                { id: 'read', label: 'Read', icon: BookOpen },
                { id: 'listen', label: 'Listen', icon: Volume2 },
                { id: 'watch', label: 'Watch', icon: Video },
              ].map(t => {
                const Icon = t.icon;
                const active = tab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    className={`px-4 py-2.5 text-xs font-semibold transition-colors flex items-center gap-1.5 border-b-2 -mb-[1px] ${
                      active
                        ? 'border-blue-600 text-blue-700'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid={`integration-guide-tab-${t.id}`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {t.label}
                  </button>
                );
              })}
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {loading && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                </div>
              )}

              {!loading && guide && tab === 'read' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-700 leading-relaxed">{guide.summary}</p>

                  {guide.fields_produced?.length > 0 && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                      <div className="text-[11px] font-semibold text-emerald-800 uppercase tracking-wider mb-1.5">
                        Keys you will end up with
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {guide.fields_produced.map(f => (
                          <Badge key={f} className="bg-white text-emerald-700 border border-emerald-300 text-[11px]">
                            {f}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  <a
                    href={guide.dashboard_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700"
                    data-testid={`integration-guide-dashboard-${provider}`}
                  >
                    Open {guide.name} dashboard <ExternalLink className="h-3 w-3" />
                  </a>

                  <ol className="space-y-3 mt-4">
                    {guide.steps.map((step, idx) => (
                      <li key={idx} className="flex gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center mt-0.5">
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">{step.title}</div>
                          <p className="text-xs text-gray-600 leading-relaxed mt-1">{step.body}</p>
                        </div>
                      </li>
                    ))}
                  </ol>

                  {guide.common_pitfalls?.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mt-4">
                      <div className="flex items-center gap-1.5 mb-2">
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                        <span className="text-[11px] font-bold text-amber-800 uppercase tracking-wider">
                          Common mistakes to avoid
                        </span>
                      </div>
                      <ul className="space-y-1.5">
                        {guide.common_pitfalls.map((p, i) => (
                          <li key={i} className="text-xs text-amber-800 leading-relaxed flex gap-1.5">
                            <span className="text-amber-500 flex-shrink-0">•</span>
                            <span>{p}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {!loading && guide && tab === 'listen' && (
                <div className="flex flex-col items-center justify-center text-center py-8 space-y-4">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${
                    playing ? 'bg-emerald-500 animate-pulse' : 'bg-blue-100'
                  }`}>
                    {playing
                      ? <Volume2 className="h-9 w-9 text-white" />
                      : <Volume2 className="h-9 w-9 text-blue-600" />}
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">
                      {playing ? 'Now playing…' : 'Listen to the full guide'}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1 max-w-md">
                      We will narrate every step out loud so you can follow along while clicking through the provider&apos;s website.
                    </p>
                  </div>
                  <Button
                    onClick={handleListen}
                    variant={playing ? 'destructive' : 'default'}
                    className="gap-1.5"
                    data-testid={`integration-guide-listen-${provider}`}
                  >
                    {playing ? <><Square className="h-4 w-4" /> Stop</> : <><Volume2 className="h-4 w-4" /> Play guide</>}
                  </Button>
                  {ttsSource && (
                    <p className="text-[10px] text-gray-400">
                      {ttsSource === 'premium' ? 'Premium AI voice — recorded once, free for everyone' : 'Using your browser voice (no internet needed)'}
                    </p>
                  )}
                  {!guide.audio_available && (
                    <div className="mt-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-[11px] text-gray-500 max-w-sm">
                      Pre-recorded audio is not yet uploaded. We will play it using your browser voice meanwhile.
                    </div>
                  )}
                  {isSuperAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleGenerateAudio}
                      disabled={generatingAudio}
                      className="text-[11px] gap-1.5 mt-2"
                      data-testid={`integration-guide-gen-audio-${provider}`}
                    >
                      {generatingAudio
                        ? <><Loader2 className="h-3 w-3 animate-spin" /> Generating…</>
                        : guide.audio_available
                          ? <>Re-record premium audio</>
                          : <>Record premium audio now</>}
                    </Button>
                  )}
                </div>
              )}

              {!loading && guide && tab === 'watch' && (
                <div className="space-y-3">
                  {guide.video_url ? (
                    <div className="aspect-video w-full bg-black rounded-xl overflow-hidden">
                      {guide.video_url.match(/\.(mp4|webm|mov)(\?|$)/i) ? (
                        <video
                          src={guide.video_url}
                          controls
                          className="w-full h-full"
                          data-testid={`integration-guide-video-${provider}`}
                        />
                      ) : (
                        <iframe
                          src={guide.video_url}
                          title={`${guide.name} setup video`}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="w-full h-full border-0"
                          data-testid={`integration-guide-video-${provider}`}
                        />
                      )}
                    </div>
                  ) : (
                    <div className="aspect-video w-full bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl flex items-center justify-center border-2 border-dashed border-slate-300">
                      <div className="text-center px-6">
                        <Video className="h-10 w-10 text-slate-400 mx-auto mb-3" />
                        <h3 className="text-sm font-semibold text-slate-700">Video coming soon</h3>
                        <p className="text-[11px] text-slate-500 mt-1 max-w-xs mx-auto">
                          Our team is recording a walkthrough for {guide.name}. Use the Read tab in the meantime — it covers every step.
                        </p>
                      </div>
                    </div>
                  )}

                  {isSuperAdmin && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] font-bold text-blue-800 uppercase tracking-wider">Super Admin — Video URL</span>
                        {!editingVideo && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingVideo(true)}
                            className="h-7 text-[11px] gap-1"
                            data-testid={`integration-guide-edit-video-${provider}`}
                          >
                            <Pencil className="h-3 w-3" /> {guide.video_url ? 'Replace' : 'Add video'}
                          </Button>
                        )}
                      </div>
                      {editingVideo ? (
                        <div className="space-y-2">
                          <Input
                            value={videoUrlDraft}
                            onChange={(e) => setVideoUrlDraft(e.target.value)}
                            placeholder="https://drive.google.com/file/d/... or YouTube / Vimeo / MP4 URL"
                            className="text-xs"
                            data-testid={`integration-guide-video-input-${provider}`}
                          />
                          <p className="text-[10px] text-blue-700/80">
                            Accepts Google Drive share links (preferred, ad-free), YouTube, Vimeo or direct .mp4 URLs.
                          </p>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={handleSaveVideo}
                              disabled={savingVideo}
                              className="text-xs gap-1.5"
                              data-testid={`integration-guide-save-video-${provider}`}
                            >
                              {savingVideo ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                              Save for all subscribers
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { setEditingVideo(false); setVideoUrlDraft(guide.video_url_raw || ''); }}
                              className="text-xs"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        guide.video_url_raw && (
                          <p className="text-[11px] text-blue-700 truncate">
                            <span className="text-blue-500">Source:</span> {guide.video_url_raw}
                          </p>
                        )
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 border-t border-gray-100 px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-[11px] text-gray-500">
                <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                <span>Once you have the keys, paste them above and click Save.</span>
              </div>
              <Button size="sm" variant="ghost" onClick={handleClose} className="text-xs" data-testid="integration-guide-done">
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default IntegrationKeyGuide;
